/**
 * Authorization Utilities - User permissions and access control
 */

import { supabase } from '@/integrations/supabase/client';

// Authorized admin users who can see security logs and contamination details
const AUTHORIZED_ADMIN_USERS = [
  'a4b379dc-3628-442b-8354-02b66d8e872f', // Primary admin user
  // Add additional authorized user IDs here
];

// Authorized admin emails (backup check)
const AUTHORIZED_ADMIN_EMAILS = [
  // Add authorized email addresses here
];

/**
 * Check if user is authorized to see security logs and contamination details
 */
export async function isAuthorizedAdmin(userId: string): Promise<boolean> {
  if (AUTHORIZED_ADMIN_USERS.includes(userId)) {
    return true;
  }

  try {
    const { data: user } = await supabase.auth.getUser();
    if (user?.user?.email && AUTHORIZED_ADMIN_EMAILS.includes(user.user.email)) {
      return true;
    }
  } catch (error) {
    console.error('Error checking user authorization:', error);
  }

  return false;
}

/**
 * Get current user ID
 */
export async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

/**
 * Check if current user can see security details
 */
export async function canSeeSecurityDetails(): Promise<boolean> {
  const userId = await getCurrentUserId();
  if (!userId) return false;
  
  return await isAuthorizedAdmin(userId);
}

/**
 * Conditionally log security information (only for authorized users)
 */
export async function secureLog(
  level: 'log' | 'warn' | 'error',
  message: string,
  data?: any
): Promise<void> {
  const canSee = await canSeeSecurityDetails();
  
  if (canSee) {
    switch (level) {
      case 'log':
        console.log(message, data);
        break;
      case 'warn':
        console.warn(message, data);
        break;
      case 'error':
        console.error(message, data);
        break;
    }
  }
}

/**
 * Safe logging that only shows generic messages to unauthorized users
 */
export async function safeLog(
  authorizedMessage: string,
  publicMessage: string,
  data?: any
): Promise<void> {
  const canSee = await canSeeSecurityDetails();
  
  if (canSee) {
    console.log(authorizedMessage, data);
  } else {
    console.log(publicMessage);
  }
}